/**
 * Iter XX.266 — Mobile bottom navigation shell.
 *
 * Five tabs, one visually elevated centre. Renders across every
 * authenticated primary route (`/chat`, `/pulse`, `/me`, ...) so the
 * player always has a single-thumb-reach path to the four primary
 * surfaces + a clean secondary menu.
 *
 *   Home | Pulse | (Gully) | Memory | More
 *
 * Design invariants (per founder brief XX.266):
 *   · Dark neutral base; Gully emerald reserved for the centre tab
 *     and the currently-selected tab indicator only.
 *   · Consistent 56px minimum tap targets; safe-area padding on the
 *     bottom edge; no border stack on the individual tabs.
 *   · Central Gully tab uses the canonical `/icons/gully-character.png`
 *     asset (do NOT regenerate). Slightly larger + emerald ring so
 *     it reads as the product's primary action from three feet away.
 *   · Never duplicates functionality — every destination reachable
 *     via this nav already exists as a route; we simply route to it.
 *   · Every existing `data-testid` on the header pills continues to
 *     work (the pills are hidden, not removed) so analytics + QA
 *     don't regress. New nav test IDs are ADDITIVE.
 *
 * Not touched: any backend, entitlement, coaching, memory, Pulse,
 * Season, Earn, Plus, RevenueCat, Stripe, IAP, Capacitor native
 * config. Frontend presentation only.
 *
 * Props:
 *   currentRoute   — string; the route the nav is being rendered for.
 *                    "home" | "chat" | "pulse" | "memory" | "more"
 *                    "home" and "chat" both live at `/chat` — the
 *                    distinction is whether `<AuthedHomeHero>` is
 *                    the top surface (Home) or the transcript is
 *                    (Chat).
 *   onHomeTap      — required. Fired when the Home tab is tapped.
 *                    Chat.jsx wires this to `setHomePinned(true)`;
 *                    other pages navigate to `/chat` first.
 *   onGullyTap     — required. Fired when the Gully centre tab is
 *                    tapped. Chat.jsx wires this to
 *                    `setHomePinned(false)` + focus composer.
 *   onMoreTap      — required. Opens the More sheet.
 *   isPlus         — boolean. Reserved (nav is neutral to Plus state
 *                    for now; the top-bar owns Plus messaging).
 */
import React, { useLayoutEffect, useRef } from "react";
import { Home, Users, Brain, Menu } from "lucide-react";
import { Link } from "react-router-dom";
// Iter XX.267b — subtle native haptics on every bottom-nav tap.
// Light for peripheral tabs; medium for the central Gully action.
import { hapticLight, hapticMedium } from "../lib/haptics";

const TABS = [
  { id: "home",   label: "Home",   icon: Home,  kind: "action", testid: "mobile-nav-home" },
  { id: "pulse",  label: "Pulse",  icon: Users, kind: "link",   to: "/pulse",       testid: "mobile-nav-pulse" },
  { id: "gully",  label: "Gully",                kind: "action-primary", testid: "mobile-nav-gully" },
  { id: "memory", label: "Memory", icon: Brain, kind: "link",   to: "/me?tab=knows", testid: "mobile-nav-memory" },
  { id: "more",   label: "More",   icon: Menu,  kind: "action", testid: "mobile-nav-more" },
];

export default function MobileBottomNav({
  currentRoute = "chat",
  onHomeTap,
  onGullyTap,
  onMoreTap,
}) {
  // For the "home vs chat" distinction, the visual selected state is
  // "home" when currentRoute === "home", else "gully" when on chat.
  const activeId =
    currentRoute === "home"   ? "home"
    : currentRoute === "chat" ? "gully"
    : currentRoute;

  // Iter XX.286 — Publish the nav's rendered height (including the iOS
  // safe-area inset) as a CSS variable on <html> so every consumer
  // surface can honour the shared clearance contract via
  // `.pb-mobile-nav` (see index.css). Measured after render via
  // getBoundingClientRect so any future height change (safe-area,
  // font-size, i18n label wrap) is picked up automatically. Cleared on
  // unmount so pages that don't render the nav are never over-padded.
  const navRef = useRef(null);
  useLayoutEffect(() => {
    const root = document.documentElement;
    if (!root || !navRef.current) return undefined;
    const publish = () => {
      try {
        const h = Math.ceil(navRef.current.getBoundingClientRect().height);
        if (Number.isFinite(h) && h > 0) {
          root.style.setProperty("--gully-bottom-nav-h", `${h}px`);
        } else {
          // Height is 0 when the nav is display:none (e.g. `sm:hidden`
          // wrapper on desktop after a mobile→desktop resize). Clear
          // the variable so pages that use `.pb-mobile-nav` /
          // `.mb-mobile-nav` don't inherit stale mobile reservation.
          root.style.removeProperty("--gully-bottom-nav-h");
        }
      } catch { /* noop */ }
    };
    publish();
    // Re-measure on viewport / safe-area / orientation changes. iOS
    // resizes the visual viewport on keyboard open/close; we honour
    // whichever value the browser exposes.
    const ro = typeof ResizeObserver !== "undefined"
      ? new ResizeObserver(publish)
      : null;
    if (ro) ro.observe(navRef.current);
    window.addEventListener("resize", publish);
    window.addEventListener("orientationchange", publish);
    return () => {
      if (ro) ro.disconnect();
      window.removeEventListener("resize", publish);
      window.removeEventListener("orientationchange", publish);
      try { root.style.removeProperty("--gully-bottom-nav-h"); } catch { /* noop */ }
    };
  }, []);

  return (
    <nav
      ref={navRef}
      data-testid="mobile-bottom-nav"
      className="fixed inset-x-0 bottom-0 z-[80] border-t border-neutral-800/80 bg-neutral-950/95 backdrop-blur supports-[backdrop-filter]:bg-neutral-950/85"
      style={{ paddingBottom: "env(safe-area-inset-bottom, 0px)" }}
      aria-label="Primary navigation"
    >
      <div className="max-w-2xl mx-auto grid grid-cols-5 items-end px-2 pt-1 pb-1">
        {TABS.map((t) => {
          const active = activeId === t.id;
          const isPrimary = t.kind === "action-primary";

          // Centre "Gully" tab — visually elevated, uses the canonical
          // character asset. Sits slightly higher so it reads as the
          // dominant action, matching the reference mock.
          if (isPrimary) {
            return (
              <button
                key={t.id}
                type="button"
                onClick={() => {
                  // Iter XX.267b — medium haptic on the central Gully
                  // tap; never awaited so navigation is not delayed.
                  hapticMedium();
                  if (typeof onGullyTap === "function") onGullyTap();
                }}
                data-testid={t.testid}
                aria-label="Talk to Gully"
                className="flex flex-col items-center justify-end gap-1 pt-1 focus:outline-none"
              >
                <div
                  className={
                    // P0-adjacent UI fix 2026-10-01 — removed `-mt-4`: the
                    // negative margin lifted the avatar above the nav bar
                    // so it read as a floating overlay covering the
                    // composer. The center tab stays the primary action
                    // (size + ring) but now sits IN the nav flow.
                    "relative w-14 h-14 rounded-full bg-neutral-900 " +
                    "ring-2 " + (active ? "ring-emerald-400" : "ring-neutral-700") + " " +
                    "flex items-center justify-center shadow-[0_4px_16px_rgba(0,0,0,0.4)] " +
                    "transition-colors"
                  }
                >
                  <img
                    src="/icons/gully-character.png"
                    alt=""
                    aria-hidden="true"
                    className="w-11 h-11 rounded-full object-cover"
                  />
                  {active && (
                    <span
                      aria-hidden="true"
                      className="absolute -bottom-0.5 -right-0.5 w-3 h-3 rounded-full bg-emerald-400 ring-2 ring-neutral-950"
                    />
                  )}
                </div>
                <span className={
                  "text-[10px] font-medium " + (active ? "text-emerald-300" : "text-neutral-400")
                }>
                  {t.label}
                </span>
              </button>
            );
          }

          const Icon = t.icon;
          const iconEl = (
            <>
              <Icon size={20} className={active ? "text-emerald-300" : "text-neutral-400"} />
              <span className={
                "text-[10px] font-medium mt-0.5 " + (active ? "text-emerald-300" : "text-neutral-400")
              }>
                {t.label}
              </span>
            </>
          );

          // Action tabs (Home / More) call handlers; link tabs (Pulse /
          // Memory) navigate.
          if (t.kind === "link") {
            return (
              <Link
                key={t.id}
                to={t.to}
                data-testid={t.testid}
                aria-current={active ? "page" : undefined}
                onClick={() => {
                  // Iter XX.267b — light haptic on Pulse/Memory taps.
                  // Fires alongside the router navigation, never
                  // delays it.
                  hapticLight();
                }}
                className="flex flex-col items-center justify-end py-1.5 min-h-[56px] focus:outline-none"
              >
                {iconEl}
              </Link>
            );
          }

          const handler = t.id === "home" ? onHomeTap : onMoreTap;
          return (
            <button
              key={t.id}
              type="button"
              onClick={() => {
                // Iter XX.267b — light haptic on Home / More.
                hapticLight();
                if (typeof handler === "function") handler();
              }}
              data-testid={t.testid}
              aria-current={active ? "page" : undefined}
              className="flex flex-col items-center justify-end py-1.5 min-h-[56px] focus:outline-none"
            >
              {iconEl}
            </button>
          );
        })}
      </div>
    </nav>
  );
}
